import { Link } from '../../../components/Link';
import { DKIM_PROVIDER_GROUPS, PROBED_SELECTOR_COUNT } from '../../../lib/dkimSelectors';
import {
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('dkim-selector');

/**
 * `/learn/dkim-selector/` — the first article in the informational layer.
 *
 * Chosen as a deliberate LONG-TAIL play rather than the head term. "dkim check" belongs to
 * MXToolbox and dmarcian and a domain with our authority will not take it; "which selector does
 * Microsoft 365 use" and "DKIM selector not found" are specific, low-competition, and already
 * drawing impressions in Search Console.
 *
 * The provider table is the reason the page is worth publishing: it is generated from
 * `lib/dkimSelectors.ts`, which a CI test pins against the API's actual probe list. It is
 * code-true rather than copied, and nobody publishes the mapping as a single table.
 */
export default function DkimSelectorPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'A DKIM selector is a short label that tells a receiving mail server which public key to fetch when it verifies your signature. It lets one domain publish several DKIM keys at once — one per sending service. The selector travels in the s= tag of every message you send, and the key itself lives in DNS at selector._domainkey.yourdomain.com.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Where the record lives">
        <p>
          A DKIM record is a TXT record at a subdomain built from the selector. The selector goes
          first, then the fixed literal <Tok>_domainkey</Tok>, then your domain:
        </p>
        <LearnCode>
          {`google._domainkey.yourdomain.com   TXT   "v=DKIM1; k=rsa; p=MIIBIjANBg…"
└─ selector    └─ fixed literal`}
        </LearnCode>
        <p>
          There is <strong className="text-txt">no record that lists your selectors.</strong> DNS
          gives you no way to ask “which DKIM keys does this domain publish?” — you can only ask
          about a name you already know. That single fact explains most of this page: finding a
          selector means either reading it off a message you sent, or guessing well.
        </p>
      </LearnSection>

      <LearnSection title="How to find your selector">
        <LearnSubsection title="From a message you sent (authoritative)">
          <p>
            Send yourself a message, open the raw source, find the <Tok>DKIM-Signature</Tok> header
            and read the <Tok>s=</Tok> tag:
          </p>
          <LearnCode>
            {`DKIM-Signature: v=1; a=rsa-sha256; c=relaxed/relaxed; d=yourdomain.com;
    s=google; t=1717171717; bh=…
      └─ selector`}
          </LearnCode>
          <LearnList>
            <li>
              <strong className="text-txt">Gmail</strong> — ⋮ → <em>Show original</em>
            </li>
            <li>
              <strong className="text-txt">Outlook</strong> — <em>File → Properties → Internet
              headers</em>
            </li>
            <li>
              <strong className="text-txt">Apple Mail</strong> — <em>View → Message → All
              Headers</em>
            </li>
          </LearnList>
          <p>
            <Tok>d=</Tok> is the signing domain and <Tok>s=</Tok> is the selector. Together they
            give you the exact record to look up.
          </p>
        </LearnSubsection>

        <LearnSubsection title="By probing a known list">
          <p>
            If you are auditing a domain you do not send from — a prospect, an acquisition, a client
            you have just onboarded — there is no header to read. The standard fallback is to probe
            a curated list of selectors, which is what our checker does, and what every other
            checker does too.
          </p>
          <p>
            <Link to="/tools/dkim-checker" className="text-accent underline underline-offset-4">
              Check a domain’s DKIM records
            </Link>{' '}
            — it probes all {PROBED_SELECTOR_COUNT} selectors below in parallel and reports every
            key it finds.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="Which selector does my provider use?">
        <p>
          This is the exact list our checker probes, grouped by who publishes each one. If your
          provider is not here, it either mints a per-account selector (see below) or has you
          publish a CNAME that points at a record it hosts.
        </p>
        <LearnTable
          head={['Provider', 'Selector(s)']}
          rows={DKIM_PROVIDER_GROUPS.map((g) => [
            <span key="p">
              {g.provider}
              {g.note && <div className="mt-1 text-[12px] text-muted">{g.note}</div>}
            </span>,
            <span key="s" className="font-mono text-[12.5px] text-muted">{g.selectors.join(', ')}</span>,
          ])}
        />
        <p className="text-[13px]">
          The table tracks our probe list rather than a provider’s current documentation. Providers
          do change selectors; when one does, the authoritative answer is still the{' '}
          <Tok>s=</Tok> tag on a message you actually sent.
        </p>

        <LearnSubsection title="The selectors nobody can guess">
          <p>
            Some providers mint a <strong className="text-txt">per-account selector</strong> — a
            random token rather than a fixed word. Amazon SES BYODKIM and several ESPs do this,
            having you publish three CNAMEs of the form{' '}
            <Tok>abc123xyz._domainkey.yourdomain.com</Tok>.
          </p>
          <p>
            No probe list will ever find those, because the selector is unique to your account. You
            have to read it from a sent message or from the provider’s dashboard. Any tool claiming
            to discover DKIM for every domain is either reading headers or overstating what DNS can
            do.
          </p>
        </LearnSubsection>
      </LearnSection>

      <LearnSection title="Why you get “DKIM selector not found”">
        <p>In rough order of how often each one is the actual cause:</p>
        <LearnList ordered>
          <li>
            <strong className="text-txt">Wrong selector.</strong> The commonest cause by a distance.
            The record is fine; you are asking for the wrong name. Read <Tok>s=</Tok> off a real
            message rather than guessing.
          </li>
          <li>
            <strong className="text-txt">You looked up the bare domain.</strong> There is no DKIM
            record at the apex. It lives one level down, under the selector.
          </li>
          <li>
            <strong className="text-txt">The CNAME is not followed.</strong> Most providers have you
            publish a CNAME pointing at a TXT record they host. If it is missing, mistyped, or your
            DNS host silently flattened it, the lookup dead-ends.
          </li>
          <li>
            <strong className="text-txt">DNS has not propagated.</strong> A new record can take up
            to the zone’s TTL. Minutes usually, an hour sometimes.
          </li>
          <li>
            <strong className="text-txt">Your DNS host appended the domain twice.</strong> Entering
            a fully-qualified name where the host expects a relative one produces{' '}
            <Tok>google._domainkey.yourdomain.com.yourdomain.com</Tok>. Check the record’s resolved
            name, not what you typed into the box.
          </li>
          <li>
            <strong className="text-txt">The key was rotated and the old selector retired.</strong>{' '}
            Mail signed with the old selector now fails while new mail passes — see rotation below.
          </li>
        </LearnList>
      </LearnSection>

      <LearnSection title="Multiple selectors, and rotating them">
        <p>Publishing several selectors at once is normal and correct — one per sending service:</p>
        <LearnCode>
          {`google._domainkey.yourdomain.com   →  Google Workspace (staff mail)
k1._domainkey.yourdomain.com       →  Mailchimp (marketing)
s1._domainkey.yourdomain.com       →  SendGrid (transactional)`}
        </LearnCode>
        <p>
          Each service signs with its own key; a receiver fetches whichever one the <Tok>s=</Tok>{' '}
          tag names. Nothing conflicts, and there is no limit worth worrying about.
        </p>
        <p>
          <strong className="text-txt">Rotation</strong> means publishing a new selector, moving
          signing to it, then retiring the old one — <em>after</em> every message signed with it has
          aged out. Retire it too early and mail still in flight fails DKIM. A common cadence is two
          selectors alternating every 6–12 months.
        </p>
        <p>
          This is also the failure DMARC reports surface first: one service starts failing DKIM
          while the rest keep passing, usually because its key was rotated at the provider and the
          DNS record was never updated.
        </p>
      </LearnSection>

      <LearnSection title="Selector vs signing domain vs DMARC alignment">
        <p>Worth separating, because these three get conflated constantly:</p>
        <LearnList>
          <li>
            <Tok>s=</Tok> <strong className="text-txt">(selector)</strong> — which key to fetch.
            Purely a lookup detail.
          </li>
          <li>
            <Tok>d=</Tok> <strong className="text-txt">(signing domain)</strong> — who claims
            responsibility for the message. This is what DMARC cares about.
          </li>
          <li>
            <strong className="text-txt">Alignment</strong> — DMARC passes on DKIM when{' '}
            <Tok>d=</Tok> matches the visible <Tok>From:</Tok> domain.{' '}
            <strong className="text-txt">The selector plays no part in alignment.</strong> Sign with
            any selector you like; what matters is that <Tok>d=</Tok> lines up.
          </li>
        </LearnList>
        <p>
          Hence a frequent misdiagnosis: “DMARC fails, so the selector must be wrong.” If DKIM
          verified at all, the selector was right — the problem is <Tok>d=</Tok> not aligning. The{' '}
          <Link to="/tools/dmarc-checker" className="text-accent underline underline-offset-4">
            DMARC checker
          </Link>{' '}
          reads the policy side of that.
        </p>
      </LearnSection>
    </>
  );
}

/**
 * FAQ answers are plain text — they are emitted verbatim as FAQPage JSON-LD, so no markup, and
 * `yourdomain.com` rather than a documentation placeholder domain (the structured-data contract
 * rejects placeholder strings in schema values, and rightly so: they assert a fact to a crawler).
 */
const FAQ = [
  {
    q: 'Can I have more than one DKIM selector?',
    a: 'Yes, and you usually should — one per sending service. Each publishes its own key and signs with its own selector. They do not conflict with each other.',
  },
  {
    q: 'Does the selector name matter?',
    a: 'Not to the protocol. Any label works as long as the TXT record exists at that selector under _domainkey, and the signature’s s= tag names it. Providers pick fixed names so their setup docs can stay static.',
  },
  {
    q: 'Why can’t a tool just list my selectors?',
    a: 'DNS has no "list the records under this name" query. You can only look up a name you already know, so discovery is either reading s= from a real message or probing a curated list. That is a limit of DNS, not of the tool.',
  },
  {
    q: 'What is _domainkey?',
    a: 'A fixed literal the DKIM spec requires between the selector and your domain. It is always present and it never changes, so it is not something you configure.',
  },
  {
    q: 'Is a 1024-bit DKIM key still fine?',
    a: 'It still verifies, but Gmail and Outlook both downrank it and may eventually stop accepting it. Use 2048-bit. Our DKIM key generator produces a 2048-bit keypair entirely in your browser.',
  },
  {
    q: 'My provider gave me a CNAME, not a TXT record. Is that wrong?',
    a: 'No, that is the normal pattern. The CNAME points at a TXT record the provider hosts, so they can rotate the key without you touching DNS. Just verify the CNAME resolves; a DKIM lookup follows it.',
  },
];
